
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { FeedbackMessage } from "@/types/feedback";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/context/PermissionsContext";

export const useDeleteMessage = (messages: FeedbackMessage[] = []) => {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const queryClient = useQueryClient();
  
  const deleteMessage = useMutation({
    mutationFn: async (id: string) => {
      if (!user) {
        console.error("No authenticated user found when trying to delete message");
        throw new Error("Je moet ingelogd zijn om een bericht te verwijderen");
      }

      // Check if user has permission to delete
      const canDelete = hasPermission("feedback", "delete");
      if (!canDelete) {
        console.error("User does not have permission to delete messages");
        throw new Error("Je hebt geen rechten om berichten te verwijderen");
      }

      console.log("Attempting to delete message:", id);
      console.log("Current user:", user);
      
      // First check if the message exists
      const { data: checkData, error: checkError } = await supabase
        .from('feedback_messages')
        .select('id, user_id')
        .eq('id', id)
        .single();
        
      if (checkError) {
        console.error("Error checking if message exists:", checkError);
        throw new Error("Bericht kan niet worden gevonden");
      }
      
      if (!checkData) {
        console.error("Message does not exist:", id);
        throw new Error("Bericht bestaat niet of is al verwijderd");
      }

      // Determine if the user is the owner of the message
      const isOwner = user.id === checkData.user_id;
      const isManagement = user.role === 'management';
      
      if (!isOwner && !isManagement) {
        console.error("User is not the owner and not management");
        throw new Error("Je hebt geen rechten om dit bericht te verwijderen");
      }
      
      // Perform the delete operation - using delete instead of update to is_deleted
      const { error } = await supabase
        .from('feedback_messages')
        .delete()
        .eq('id', id);
      
      if (error) {
        console.error("Supabase delete error:", error);
        throw new Error(`Fout bij het verwijderen van bericht: ${error.message}`);
      }
      
      // If there was no error, consider the operation successful
      console.log("Delete successful for message ID:", id);
      
      // Return a success object with the deleted id
      return { id };
    },
    onSuccess: (data) => {
      console.log("Message deleted successfully:", data);
      queryClient.invalidateQueries({ queryKey: ['feedback-messages'] });
      toast.success('Bericht succesvol verwijderd');
    },
    onError: (error: any) => {
      console.error('Error deleting feedback message:', error);
      toast.error(`${error.message || "Onbekende fout"}`);
    }
  });

  return { deleteMessage, user };
};
